import { mdiCheck } from '@mdi/js';
import * as React from 'react';
import { Popover } from '../popover';
import { Tooltip } from '../tooltip';
import * as S from './icon-select.styles';

export interface IIconSelectOption {
    value: string | undefined;
    label: string;
    icon?: string;
    iconColor?: string;
}

export interface IIconSelectProps {
    options: IIconSelectOption[];
    value?: string;
    onChange?: (value: string | undefined) => void;
    multiple?: boolean;
    selectedValues?: string[];
    onMultiChange?: (values: string[]) => void;
    buttonIcon?: string;
    buttonIconColor?: string;
    disabled?: boolean;
    className?: string;
    loading?: boolean;
    tooltip?: string;
}

export const IconSelect: React.FunctionComponent<IIconSelectProps> = (props: IIconSelectProps): React.ReactElement => {
    const { 
        options, 
        value, 
        onChange, 
        multiple = false,
        selectedValues = [],
        onMultiChange,
        buttonIcon,
        buttonIconColor,
        disabled, 
        className,
        loading = false,
        tooltip
    } = props;
    const [menuOpen, setMenuOpen] = React.useState(false);

    const selectedOption = React.useMemo(() => {
        return options.find(opt => opt.value === value);
    }, [options, value]);

    const defaultOption = React.useMemo(() => {
        return options.find(opt => opt.value === undefined);
    }, [options]);

    const buttonOption = selectedOption || defaultOption;

    const selectedCount = multiple ? selectedValues.filter(v => v !== undefined).length : 0;

    const handleOptionClick = (option: IIconSelectOption) => {
        if (disabled) return;

        if (multiple && onMultiChange) {
            const optValue = option.value;
            if (optValue === undefined) return;
            
            const isSelected = selectedValues.includes(optValue);
            const newValues = isSelected
                ? selectedValues.filter(v => v !== optValue)
                : [...selectedValues, optValue];
            
            onMultiChange(newValues);
        } else if (onChange) {
            onChange(option.value);
            setMenuOpen(false);
        }
    };

    const renderButton = (): React.ReactElement => {
        if (multiple) {
            return (
                <S.IconButtonWrapper>
                    <S.IconButton disabled={disabled} aria-label="Select options">
                        {buttonIcon && (
                            <S.ButtonIcon
                                path={buttonIcon}
                                color={disabled ? undefined : buttonIconColor}
                            />
                        )}
                    </S.IconButton>
                    {selectedCount > 0 && <S.CountBadge>{selectedCount}</S.CountBadge>}
                </S.IconButtonWrapper>
            );
        } else {
            return (
                <S.IconButton disabled={disabled} aria-label="Select priority">
                    {buttonOption && buttonOption.icon && (
                        <S.ButtonIcon
                            path={buttonOption.icon}
                            color={disabled ? undefined : buttonOption.iconColor}
                        />
                    )}
                </S.IconButton>
            );
        }
    };

    const isOptionSelected = (option: IIconSelectOption): boolean => {
        if (multiple) {
            return option.value !== undefined && selectedValues.includes(option.value);
        }
        return option.value === value;
    };

    const popover = (
        <Popover
            renderButton={renderButton}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            align="left"
            verticalAlign="bottom"
            className={className}>
            <S.MenuContainer>
                {options.length === 0 && !loading ? (
                    <S.EmptyMessage>No options available</S.EmptyMessage>
                ) : (
                    options.map((option, index) => {
                        const selected = isOptionSelected(option);
                        const hasIcon = option.icon !== undefined;
                        
                        return (
                            <S.MenuItem
                                key={`${option.value}-${index}`}
                                selected={selected}
                                disabled={disabled || loading}
                                hasIcon={hasIcon}
                                onClick={() => handleOptionClick(option)}>
                                {hasIcon && (
                                    <S.MenuItemIcon path={option.icon!} color={option.iconColor} />
                                )}
                                <S.MenuItemLabel>{option.label}</S.MenuItemLabel>
                                {selected && <S.SelectedIcon path={mdiCheck} />}
                            </S.MenuItem>
                        );
                    })
                )}
                {loading && (
                    <S.LoadingOverlay>
                        <S.Spinner />
                    </S.LoadingOverlay>
                )}
            </S.MenuContainer>
        </Popover>
    );

    if (tooltip) {
        return (
            <Tooltip direction="top" title={<span>{tooltip}</span>}>
                {popover}
            </Tooltip>
        );
    }

    return popover;
};
